import { useMemo, useState } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect } from 'react-admin';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, InputAdornment, MenuItem, FormControlLabel, Switch, Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  tbl, btn, inputSx, badgeStyle, clickableA11y,
  WEBSITE_PUBLICATION_STATE, WEBSITE_FAMILY_LABELS, WEBSITE_MAPPING_REVIEW_STATE,
} from '../styles/ds';
import EmptyState from '../components/EmptyState';

const Toolbar = () => (
  <TopToolbar><CreateButton label="מוצר אתר חדש" sx={btn.create} /></TopToolbar>
);

export const listingState = p => (p.retired_at ? 'retired' : p.publication_state);

const ProductTable = () => {
  const { data = [], isLoading, filterValues, setFilters } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState('');
  const [family, setFamily] = useState('');
  const [state, setState] = useState('');
  const showRetired = filterValues?.retired === 'true';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter(p =>
      (!q || p.name_en.toLowerCase().includes(q) || p.slug.includes(q) || (p.script_code || '').toLowerCase().includes(q))
      && (!family || p.family === family)
      && (!state || p.publication_state === state));
  }, [data, search, family, state]);

  if (isLoading) return null;
  return (
    <Box>
      <Stack className="crm-list-search" direction="row" px={1} py={1.5} gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="חיפוש לפי שם, כתובת או כתב..." aria-label="חיפוש מוצרי אתר"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>, sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 280, maxWidth: '100%' }} />
        <TextField select size="small" label="משפחה" value={family} onChange={e => setFamily(e.target.value)} sx={{ minWidth: 160, ...inputSx }}>
          <MenuItem value="">הכול</MenuItem>
          {Object.entries(WEBSITE_FAMILY_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
        </TextField>
        {!showRetired && (
          <TextField select size="small" label="מצב" value={state} onChange={e => setState(e.target.value)} sx={{ minWidth: 140, ...inputSx }}>
            <MenuItem value="">הכול</MenuItem>
            {['draft', 'published', 'unpublished'].map(k => <MenuItem key={k} value={k}>{WEBSITE_PUBLICATION_STATE[k].label}</MenuItem>)}
          </TextField>
        )}
        <FormControlLabel label="הצג מוצרים שהוצאו משימוש"
          control={<Switch checked={showRetired} onChange={e => { setState(''); setFilters(e.target.checked ? { retired: 'true' } : {}, null, false); }} />} />
      </Stack>

      {filtered.length === 0 ? (
        <EmptyState title={search || family || state ? 'לא נמצאו תוצאות' : (showRetired ? 'אין מוצרים שהוצאו משימוש' : 'אין עדיין מוצרי אתר')} />
      ) : (
        <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
            <TableHead><TableRow>
              <TableCell>שם באתר</TableCell>
              <TableCell align="center">מצב</TableCell>
              <TableCell align="center">מיפוי</TableCell>
              <TableCell align="center">בעיות פתוחות</TableCell>
              <TableCell>משפחה</TableCell>
              <TableCell>כתב</TableCell>
              <TableCell>קטגוריה</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.map((p, i) => (
                <TableRow key={p.id} onClick={() => redirect('edit', 'website_products', p.id)}
                  {...clickableA11y(() => redirect('edit', 'website_products', p.id), { ariaLabel: `פתיחת מוצר אתר ${p.name_en}` })}
                  sx={tbl.bodyRow(i)}>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={14} dir="ltr" textAlign="right">{p.name_en}</Typography>
                    <Typography fontSize={11} color="text.secondary" dir="ltr" textAlign="right">{p.slug}</Typography>
                  </TableCell>
                  <TableCell align="center"><span style={badgeStyle(WEBSITE_PUBLICATION_STATE, listingState(p))}>{WEBSITE_PUBLICATION_STATE[listingState(p)]?.label}</span></TableCell>
                  <TableCell align="center"><span style={badgeStyle(WEBSITE_MAPPING_REVIEW_STATE, p.mapping_review_state)}>{WEBSITE_MAPPING_REVIEW_STATE[p.mapping_review_state]?.label}</span></TableCell>
                  <TableCell align="center">
                    <Typography fontSize={13} fontWeight={p.open_issues ? 700 : 400} color={p.open_issues ? 'error.main' : 'text.secondary'}>{p.open_issues || 0}</Typography>
                  </TableCell>
                  <TableCell><Typography fontSize={13}>{WEBSITE_FAMILY_LABELS[p.family]}</Typography></TableCell>
                  <TableCell><Typography fontSize={13} color="text.secondary">{p.script_code || '—'}</Typography></TableCell>
                  <TableCell><Typography fontSize={13} color="text.secondary" dir="ltr" textAlign="right">{p.category_name}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Box>
        </TableContainer>
      )}
    </Box>
  );
};

const WebsiteProductList = () => (
  <List className="crm-mobile-list crm-inline-list" sort={{ field: 'sort_position', order: 'ASC' }} perPage={1000}
    pagination={false} storeKey={false} actions={<Toolbar />} title="מוצרי אתר" sx={tbl.listSx}>
    <ProductTable />
  </List>
);
export default WebsiteProductList;
